import { useState } from 'react'
import PageTitle from '../components/fragments/PageTitle'
import Button from '../components/common/Button'
import { geocodeAddressAll } from '../lib/script'

const PRESET_ADDRESSES = [
    { label: 'Full street address', address: '1600 Amphitheatre Parkway, Mountain View, CA 94043' },
    { label: 'City + state', address: 'Dallas, TX' },
    { label: 'ZIP only', address: '75201' },
]

function waitForGoogleMaps(timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
        if (window.google?.maps?.Geocoder) {
            resolve()
            return
        }

        const startedAt = Date.now()
        const interval = setInterval(() => {
            if (window.google?.maps?.Geocoder) {
                clearInterval(interval)
                resolve()
                return
            }

            if (Date.now() - startedAt >= timeoutMs) {
                clearInterval(interval)
                reject(new Error('Google Maps failed to load. Check REACT_APP_GOOGLE_MAP_KEY.'))
            }
        }, 100)
    })
}

export default function GeocodeTest() {
    const [address, setAddress] = useState(PRESET_ADDRESSES[0].address)
    const [output, setOutput] = useState(null)
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    async function runGeocode(nextAddress = address) {
        setIsLoading(true)
        setError(null)
        setOutput(null)

        try {
            await waitForGoogleMaps()
            const data = await geocodeAddressAll(nextAddress)
            setOutput(data)
        } catch (err) {
            setError(err.message || 'Geocoding failed.')
        } finally {
            setIsLoading(false)
        }
    }

    function selectPreset(preset) {
        setAddress(preset.address)
        runGeocode(preset.address)
    }

    return (
        <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>
            <PageTitle>Geocode Test</PageTitle>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <input
                    type="text"
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    placeholder="Enter a US address"
                    style={{
                        flex: 1,
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                    }}
                />
                <Button click={() => runGeocode()} isLoading={isLoading}>
                    Geocode
                </Button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
                {PRESET_ADDRESSES.map((preset) => (
                    <button
                        key={preset.label}
                        type="button"
                        onClick={() => selectPreset(preset)}
                        disabled={isLoading}
                        style={{
                            padding: '6px 10px',
                            fontSize: '13px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            background: '#f7f7f7',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            {error && (
                <pre style={{
                    padding: '16px',
                    background: '#fff4f4',
                    border: '1px solid #f5c2c2',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '13px',
                }}>
                    {error}
                </pre>
            )}

            {output && (
                <>
                    <div style={{
                        padding: '16px',
                        marginBottom: '16px',
                        background: output.hasMultipleLocationTypes ? '#fff8e6' : '#f0f7ff',
                        border: `1px solid ${output.hasMultipleLocationTypes ? '#f0c36d' : '#b8d4f0'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                    }}>
                        <strong>Location type summary</strong>
                        <div style={{ marginTop: '8px' }}>
                            {output.resultCount === 0 && 'No results returned.'}
                            {output.resultCount === 1 && (
                                <>Single result with location type: <code>{output.locationTypes[0] || 'UNKNOWN'}</code></>
                            )}
                            {output.resultCount > 1 && !output.hasMultipleLocationTypes && (
                                <>
                                    {output.resultCount} results, all with the same location type:{' '}
                                    <code>{output.locationTypes[0] || 'UNKNOWN'}</code>
                                </>
                            )}
                            {output.hasMultipleLocationTypes && (
                                <>
                                    Multiple results with <strong>different location types</strong> ({output.resultCount} total):
                                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                                        {output.locationTypes.map((type) => (
                                            <li key={type}>
                                                <code>{type}</code> — {output.locationTypeBreakdown[type]} result(s)
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>

                    {output.hasMultipleLocationTypes && (
                        <div style={{ marginBottom: '16px' }}>
                            {output.locationTypes.map((type) => (
                                <div
                                    key={type}
                                    style={{
                                        marginBottom: '12px',
                                        padding: '12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        background: '#fafafa',
                                    }}
                                >
                                    <strong><code>{type}</code></strong> ({output.resultsByLocationType[type]?.length || 0})
                                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                                        {output.resultsByLocationType[type]?.map((result) => (
                                            <li key={result.place_id}>
                                                {result.formatted_address} — {result.lat}, {result.lng}
                                                {result.partial_match ? ' (partial match)' : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    <pre style={{
                        padding: '16px',
                        background: '#f7f7f7',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        overflow: 'auto',
                        fontSize: '13px',
                        lineHeight: 1.5,
                    }}>
                        {JSON.stringify(output, null, 2)}
                    </pre>
                </>
            )}
        </div>
    )
}
